import { PERMISSIONS, type Permission } from '../constants/permissions';

// frontend routes
const ROUTES = {
  root: '/',
  notFound: '*',
  errorPage: 'error',
  locations: 'locations',

  // Location-scoped top-level views
  yearOverview: 'locations/:locationId/year-overview',
  weekOverview: 'locations/:locationId/week-overview',
  economics: 'locations/:locationId/economics',
  patients: 'locations/:locationId/patients',

  // Location-scoped parents (settings & bookings live under the active location)
  locationSettings: 'locations/:locationId/settings',
  locationBookings: 'locations/:locationId/bookings',

  // Account-level (global) routes — no :locationId in the path
  configuration: 'configuration',
  treatmentPlanBuilder: 'configuration/treatment-plans/:templateId',
  configurationInterventions: 'configuration/interventions',
  settings: 'settings',

  // Relative child segments — settings (reused for both the global /settings/*
  // routes and the location-scoped /locations/:id/settings/* routes)
  settingsRooms: 'rooms',
  settingsInterventionRooms: 'intervention-rooms',
  settingsEmployees: 'employees',
  settingsOpeningDays: 'opening-days',
  settingsRoomNeeds: 'room-needs',
  settingsInterventionRoomProperties: 'intervention-room-properties',
  settingsStaffCompetences: 'staff-competences',
  settingsUsers: 'users',
  settingsLocations: 'locations',

  // Relative child segments — bookings
  bookings: 'bookings',
  bookingsRoomBookings: 'room-bookings',
  bookingsStaffBookings: 'staff-bookings',
  bookingsClosedDays: 'closed-days',
  bookingsReservedStaffTime: 'reserved-staff-time',
  bookingsPatientBookings: 'patient-bookings'
} as const;

// Location-scoped page segments, for building links without repeating the
// path literals that also appear in the route patterns above.
export const LOCATION_SEGMENTS = {
  yearOverview: 'year-overview',
  weekOverview: 'week-overview',
  patients: 'patients',
  economics: 'economics'
} as const;

export type LocationSegment = (typeof LOCATION_SEGMENTS)[keyof typeof LOCATION_SEGMENTS];

// The view a location selector / location card lands on by default.
export const DEFAULT_LOCATION_VIEW = LOCATION_SEGMENTS.yearOverview;

// Query param carrying the selected year on the year overview. The year lives
// in the URL so it survives page refresh and location switches.
export const YEAR_QUERY_PARAM = 'year';

// Every page carries the permission its route guard requires — the same one the
// backend requires on the endpoints the page calls — so the menus and the
// router cannot disagree about who may open what. A page is guarded on its
// READ half: reading is what opening it does, and the write controls inside are
// gated separately on the matching WRITE permission. Opening days is the one
// exception (see LOCATION_SETTINGS_PERMISSIONS).
export interface GuardedPage {
  segment: string;
  permission: Permission;
}

// The location-scoped top-level views. Keyed by segment so the router and the
// navigation look the permission up from the same place.
export const LOCATION_VIEW_PERMISSIONS: Record<LocationSegment, Permission> = {
  [LOCATION_SEGMENTS.yearOverview]: PERMISSIONS.YearPlanRead,
  [LOCATION_SEGMENTS.weekOverview]: PERMISSIONS.YearPlanRead,
  [LOCATION_SEGMENTS.patients]: PERMISSIONS.PatientRead,
  [LOCATION_SEGMENTS.economics]: PERMISSIONS.EconomicsRead
};

// The pages among `pages` whose permission is in `granted`, in the given order.
export const permittedPages = (
  pages: readonly GuardedPage[],
  granted: readonly Permission[]
): GuardedPage[] => pages.filter(({ permission }) => granted.includes(permission));

export const pagePermissions = (pages: readonly GuardedPage[]): Permission[] =>
  pages.map(({ permission }) => permission);

// Settings subpages split by backend scope. Location settings are per-location
// (carry :locationId) and sit behind location permissions, opening days
// excepted (see below); global settings are account-wide and sit behind account
// permissions.
export const LOCATION_SETTINGS_PERMISSIONS: readonly GuardedPage[] = [
  { segment: ROUTES.settingsRooms, permission: PERMISSIONS.PatientRoomsRead },
  { segment: ROUTES.settingsInterventionRooms, permission: PERMISSIONS.InterventionRoomsRead },
  { segment: ROUTES.settingsEmployees, permission: PERMISSIONS.StaffRead },
  // The account-scoped LOCATION_ADMIN rather than a location READ permission:
  // the opening days decide which dates the solver may plan on, so only holders
  // of LOCATION_ADMIN may read or change them, matching both backend endpoints.
  { segment: ROUTES.settingsOpeningDays, permission: PERMISSIONS.LocationAdmin }
];

export const LOCATION_SETTINGS_SEGMENTS: readonly string[] = LOCATION_SETTINGS_PERMISSIONS.map(
  ({ segment }) => segment
);

// The global subpages exist to edit the account's shared configuration, so they
// are guarded on the WRITE half rather than the READ one: a reader has nothing
// to do on them.
export const GLOBAL_SETTINGS_PERMISSIONS: readonly GuardedPage[] = [
  // Centers come first: everything else in settings is configured per center or
  // assigned to one, so this is where an administrator starts.
  { segment: ROUTES.settingsLocations, permission: PERMISSIONS.LocationAdmin },
  { segment: ROUTES.settingsRoomNeeds, permission: PERMISSIONS.PatientRoomPropertiesWrite },
  {
    segment: ROUTES.settingsInterventionRoomProperties,
    permission: PERMISSIONS.InterventionRoomPropertiesWrite
  },
  { segment: ROUTES.settingsStaffCompetences, permission: PERMISSIONS.StaffProfessionWrite },
  { segment: ROUTES.settingsUsers, permission: PERMISSIONS.UserAdmin }
];

export const GLOBAL_SETTINGS_SEGMENTS: readonly string[] = GLOBAL_SETTINGS_PERMISSIONS.map(
  ({ segment }) => segment
);

export const BOOKINGS_SEGMENTS = [
  ROUTES.bookingsRoomBookings,
  ROUTES.bookingsStaffBookings,
  ROUTES.bookingsClosedDays,
  ROUTES.bookingsReservedStaffTime,
  ROUTES.bookingsPatientBookings
] as const;

// The pattern every location-scoped route sits under. Matched (rather than read
// off useParams) wherever the :locationId is needed from a component mounted
// above the route that declares it — a pathless layout or guard route only sees
// the params its own ancestors matched.
export const LOCATION_ROUTE_PATTERN = '/locations/:locationId/*';

// Builds an absolute path under a specific location, e.g.
// locationPath(id, 'settings', 'patients') -> /locations/<id>/settings/patients
export const locationPath = (locationId: string, ...segments: string[]): string =>
  `/locations/${encodeURIComponent(locationId)}${
    segments.length ? `/${segments.join('/')}` : ''
  }`;

export default ROUTES;
